import React, {FC} from "react"
import Card from "@irati/ui/src/Layout/Card"
import Flex from "@irati/ui/src/Flex"
import {useTheme} from "@irati/ui/src/global/useTheme"
import FormattedMessage, {injectIntl, InjectIntl} from "@irati/utils/lang"
import Icon, {IconTypes} from "@irati/ui/src/Icon"

import Spacing from "@irati/ui/src/Spacing"
import {SelfSignUpScreens} from "../../containers/SignUp"
import {RelativeBody, StatusDot, Line, StyledLabel} from "./styled"

interface Props extends InjectIntl {
  screen: SelfSignUpScreens
  updateScreen: (updatedScreen: SelfSignUpScreens) => void
}

const SignUpProgressBanner: FC<Props> = ({screen, updateScreen, formatMessage}) => {
  const getActiveStep = () => {
    if (screen === SelfSignUpScreens.ConfirmPackageScreen) {
      return 0
    }
    if (screen === SelfSignUpScreens.KnowYourBusinessScreen) {
      return 1
    }
    if (screen === SelfSignUpScreens.KnowYourCustomerScreen) {
      return 2
    }
    return 3
  }
  const activeStep = getActiveStep()

  const progress = [
    {
      title: formatMessage({id: "platform.selfSignUp.progressBar.step1"}),
      icon: IconTypes.circleTick,
      screen: SelfSignUpScreens.ConfirmPackageScreen
    },
    {
      title: formatMessage({id: "platform.selfSignUp.progressBar.step2"}),
      icon: IconTypes.verifyBusiness,
      screen: SelfSignUpScreens.KnowYourBusinessScreen
    },
    {
      title: formatMessage({id: "platform.selfSignUp.progressBar.step3"}),
      icon: IconTypes.verifyIdentity,
      screen: SelfSignUpScreens.KnowYourCustomerScreen
    },
    {
      title: formatMessage({id: "platform.selfSignUp.progressBar.step4"}),
      icon: IconTypes.payments,
      screen: SelfSignUpScreens.PaymentScreen
    }
  ]
  const theme = useTheme()

  const getIconColor = (i: number) => {
    if (activeStep === i) {
      return theme.colors.primary1
    } else if (activeStep > i) {
      return theme.colors.greys.white
    }
    return theme.colors.greys.grey12
  }

  const handleClick = (updatedScreen: string, i: number) => {
    if (i <= activeStep) {
      updateScreen(updatedScreen)
    }
  }

  return (
    <Card bgColor={theme.colors.greys.white} marginTop={0.5} marginBottom={3}>
      <Spacing marginBottom={4} />
      <Flex justifyContent="space-between">
        {progress.map((p, i) => {
          const {title, icon} = p
          const completedStep = i < activeStep
          return (
            <>
              <StatusDot completed={completedStep} current={activeStep === i} onClick={() => handleClick(p.screen, i)}>
                <Icon icon={icon} color={getIconColor(i)} size={16} />
                <RelativeBody firstItem={i === 0} lastItem={i === 3}>
                  <StyledLabel
                    completed={completedStep}
                    color={completedStep || activeStep === i ? theme.colors.primary1 : theme.colors.greys.grey50}
                    weight={activeStep === i ? "bold" : "default"}>
                    <FormattedMessage id={title} />
                  </StyledLabel>
                </RelativeBody>
              </StatusDot>
              {i < progress.length - 1 && <Line completed={completedStep} step={progress.length} />}
            </>
          )
        })}
      </Flex>
    </Card>
  )
}

export default injectIntl(SignUpProgressBanner)
